import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDButton from "components/MDButton";
import { UploadMyFileRecordCall } from "../../redux/api/uploads.api";
import {
  ArchivePublicPresentationImageCall,
  GenerateTeachingImageCall,
  GetMyPresentationImagesCall,
  GetPublicPresentationImagesCall,
  SavePublicPresentationImageCall,
  SuggestPublicPresentationImageCall,
  UpdatePublicPresentationImageCall,
} from "../../redux/api/presentationImages.api";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#fff" },
};

export default function PublicImagesPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [subject, setSubject] = useState("");
  const [structure, setStructure] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [prompt, setPrompt] = useState("");
  const [libraryFilter, setLibraryFilter] = useState("all");
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [publicRes, privateRes] = await Promise.all([
      GetPublicPresentationImagesCall(),
      GetMyPresentationImagesCall(),
    ]);
    const publicItems = Array.isArray(publicRes?.data) ? publicRes.data : [];
    const privateItems = Array.isArray(privateRes?.data) ? privateRes.data : [];
    const byId = new Map();
    [...privateItems, ...publicItems].forEach((item) => {
      if (item?.id) byId.set(item.id, item);
    });
    setItems(Array.from(byId.values()));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setFile(null);
    setTitle("");
    setDescription("");
    setTags("");
    setSubject("");
    setStructure("");
    setVisibility("private");
    setPrompt("");
    setEditingId(null);
    setPreviewUrl("");
    setError("");
  };

  const suggest = async () => {
    if (!file) return;
    setSaving(true);
    setError("");
    try {
      const uploaded = await UploadMyFileRecordCall(file, "microlab_public_image");
      if (!uploaded?.id) throw new Error(t("pages.presentationShare.loadError"));
      const res = await SuggestPublicPresentationImageCall({ file_id: uploaded.id });
      if (res?.data?.suggestion) setDescription(res.data.suggestion);
      setFile({ ...file, uploadedId: uploaded.id, preview: uploaded.url });
    } catch (err) {
      setError(err?.message || t("pages.presentationShare.loadError"));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        const res = await UpdatePublicPresentationImageCall(editingId, {
          title,
          description,
          tags,
          subject,
          structure,
        });
        if (!res?.data) throw new Error(res?.message || t("pages.presentationShare.loadError"));
      } else {
        let fileId = file?.uploadedId;
        if (!fileId) {
          if (!file) throw new Error("Choose an image.");
          const uploaded = await UploadMyFileRecordCall(file, "microlab_public_image");
          fileId = uploaded?.id;
        }
        const res = await SavePublicPresentationImageCall({
          file_id: fileId,
          title,
          description,
          tags,
          subject,
          structure,
          visibility,
        });
        if (!res?.data) throw new Error(res?.message || t("pages.presentationShare.loadError"));
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err?.message || t("pages.presentationShare.loadError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ px: { xs: 2, md: 3 }, pb: 6, maxWidth: 880 }}>
        <Typography sx={{ fontSize: 28, fontWeight: 650, letterSpacing: "-0.03em", color: "#0f172a" }}>
          {t("pages.presentationImages.adminTitle")}
        </Typography>
        <Typography sx={{ mt: 0.6, mb: 2.5, color: "#64748b", maxWidth: 560 }}>
          {t("pages.presentationImages.adminSubtitle")}
        </Typography>

        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid rgba(226,232,240,1)",
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          {!editingId ? (
            <MDButton
              variant="text"
              color="info"
              component="label"
              sx={{ textTransform: "none", fontWeight: 650, alignSelf: "flex-start" }}
            >
              {file?.name || t("pages.presentationImages.chooseImage")}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(event) => {
                  const next = event.target.files?.[0] || null;
                  setFile(next);
                  setPreviewUrl(next ? URL.createObjectURL(next) : "");
                  setEditingId(null);
                }}
              />
            </MDButton>
          ) : null}
          {previewUrl && !editingId ? (
            <Box
              component="img"
              src={previewUrl}
              alt=""
              sx={{ width: 120, height: 80, objectFit: "cover", borderRadius: 2, bgcolor: "#f8fafc" }}
            />
          ) : null}
          <TextField
            size="small"
            label={t("pages.presentationImages.titleOptional")}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            sx={fieldSx}
          />
          <TextField
            size="small"
            multiline
            minRows={2}
            label={t("pages.presentationImages.description")}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            sx={fieldSx}
          />
          <TextField
            size="small"
            label={t("pages.presentationImages.tags")}
            value={tags}
            onChange={(event) => setTags(event.target.value)}
            sx={fieldSx}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              select
              size="small"
              label={t("pages.presentationImages.subject")}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              SelectProps={{ native: true }}
              sx={{ ...fieldSx, flex: 1 }}
            >
              <option value="" />
              {["histology", "anatomy", "embryology", "pathology", "physiology", "microbiology", "immunology", "biochemistry", "pharmacology", "usmle", "other"].map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t("pages.presentationImages.structure")}
              value={structure}
              onChange={(event) => setStructure(event.target.value)}
              sx={{ ...fieldSx, flex: 1 }}
            />
          </Box>
          <TextField
            select
            size="small"
            label={t("pages.presentationImages.visibility")}
            value={visibility}
            onChange={(event) => setVisibility(event.target.value)}
            SelectProps={{ native: true }}
            sx={fieldSx}
          >
            <option value="private">{t("pages.presentationImages.privateStatus")}</option>
            <option value="public">{t("pages.presentationImages.publicStatus")}</option>
          </TextField>
          <TextField
            size="small"
            multiline
            minRows={2}
            label={t("pages.presentationImages.generatePrompt")}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            sx={fieldSx}
          />
          {error ? (
            <Typography sx={{ fontSize: 13, color: "#e11d48" }}>{error}</Typography>
          ) : null}
          <Box sx={{ display: "flex", gap: 1 }}>
            {!editingId ? (
              <MDButton
                variant="text"
                color="info"
                disabled={!file || saving}
                onClick={suggest}
                sx={{ textTransform: "none" }}
              >
                {t("pages.presentationImages.suggest")}
              </MDButton>
            ) : null}
            <MDButton
              variant="text"
              color="info"
              disabled={saving || prompt.trim().length < 8}
              onClick={async () => {
                setSaving(true);
                setError("");
                try {
                  const res = await GenerateTeachingImageCall({ prompt });
                  if (!res?.data) throw new Error(res?.message || t("pages.presentationShare.loadError"));
                  setDescription(res.data.suggested_description || "");
                  setTitle(res.data.title || "");
                  setSubject(res.data.subject || "");
                  setStructure(res.data.structure || "");
                  setTags(res.data.tags || "");
                  setVisibility("private");
                  setFile({ uploadedId: res.data.file_id, preview: res.data.url, name: "AI illustration" });
                  await load();
                } catch (err) {
                  setError(err?.message || t("pages.presentationShare.loadError"));
                } finally {
                  setSaving(false);
                }
              }}
              sx={{ textTransform: "none" }}
            >
              {t("pages.presentationImages.generateAction")}
            </MDButton>
            <MDButton
              variant="contained"
              color="info"
              disabled={saving || description.trim().length < 8 || (!editingId && !file)}
              onClick={save}
              sx={{ textTransform: "none", borderRadius: 999, boxShadow: "none" }}
            >
              {t("pages.presentationImages.save")}
            </MDButton>
            {editingId ? (
              <MDButton variant="text" color="secondary" onClick={resetForm} sx={{ textTransform: "none" }}>
                {t("pages.presentationImages.cancel")}
              </MDButton>
            ) : null}
          </Box>
        </Box>

        {loading ? (
          <CircularProgress size={22} />
        ) : items.length === 0 ? (
          <Typography sx={{ color: "#94a3b8" }}>{t("pages.presentationImages.empty")}</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <TextField
              select
              size="small"
              value={libraryFilter}
              onChange={(event) => setLibraryFilter(event.target.value)}
              SelectProps={{ native: true }}
              sx={{ ...fieldSx, maxWidth: 220 }}
            >
              <option value="all">All</option>
              <option value="public">{t("pages.presentationImages.publicStatus")}</option>
              <option value="private">{t("pages.presentationImages.privateStatus")}</option>
            </TextField>
            {items
              .filter((item) => libraryFilter === "all" || item.scope === libraryFilter)
              .map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  p: 1.25,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  border: "1px solid rgba(226,232,240,1)",
                }}
              >
                <Box
                  component="img"
                  src={item.url}
                  alt=""
                  sx={{ width: 92, height: 68, objectFit: "cover", borderRadius: 2, bgcolor: "#f8fafc" }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>
                      {item.title || t("pages.presentationImages.untitled")}
                    </Typography>
                    <Typography
                      sx={{
                        px: 0.8,
                        py: 0.15,
                        borderRadius: 999,
                        bgcolor: "#f0fdf4",
                        color: "#15803d",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {item.scope === "public"
                        ? t("pages.presentationImages.publicStatus")
                        : t("pages.presentationImages.privateStatus")}
                    </Typography>
                  </Box>
                  <Typography sx={{ mt: 0.3, color: "#475569", fontSize: 13, lineHeight: 1.4 }}>
                    {item.description}
                  </Typography>
                  {item.tags ? (
                    <Typography sx={{ mt: 0.4, color: "#94a3b8", fontSize: 12 }}>{item.tags}</Typography>
                  ) : null}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <MDButton
                    variant="text"
                    color="info"
                    onClick={() => {
                      setEditingId(item.id);
                      setTitle(item.title || "");
                      setDescription(item.description || "");
                      setTags(item.tags || "");
                      setSubject(item.subject || "");
                      setStructure(item.structure || "");
                      setVisibility(item.scope === "public" ? "public" : "private");
                      setFile(null);
                      setPreviewUrl("");
                    }}
                    sx={{ textTransform: "none", minWidth: "auto" }}
                  >
                    {t("pages.presentationImages.edit")}
                  </MDButton>
                  <MDButton
                    variant="text"
                    color="secondary"
                    onClick={async () => {
                      await ArchivePublicPresentationImageCall(item.id);
                      if (editingId === item.id) resetForm();
                      load();
                    }}
                    sx={{ textTransform: "none", minWidth: "auto", color: "#94a3b8" }}
                  >
                    {t("pages.presentationImages.archive")}
                  </MDButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </DashboardLayout>
  );
}
